/**
 * Monoline UI — taqsimot va voronka.
 *
 * NIMA UCHUN BULAR RECHARTS EMAS
 * Loyihada grafik kutubxonasi bor (recharts) va vaqt qatorlari aynan
 * unda chiziladi. Lekin bu ikkisi — grafik emas, MATNLI RO'YXAT:
 *   · ular 5–12 qatordan iborat va har qatorda o'qiladigan yorliq bor;
 *   · ular BOSILADI — "Fishing (8)" qatoriga bosilganda ro'yxat filtrlanadi;
 *   · ular klaviatura bilan aylanib chiqilishi kerak.
 * SVG ichidagi `<rect>` ni tugmaga aylantirish, unga fokus halqasi berish va
 * ekran o'quvchiga tushuntirish — bularning hammasi oddiy `div` da tekin
 * keladi. Shuning uchun bu yerda DOM: `flex` + token, tashqi kutubxonasiz.
 *
 * MONOXROM QOIDA (DESIGN.md 4)
 * Bo'laklar RANG bilan emas, siyoh SHAFFOFLIGI bilan ajraladi: birinchi
 * qator to'q, keyingilari asta so'nadi. Rang faqat status ohangi ATAYLAB
 * berilganda qo'shiladi (`tone: 'critical'` — "javob bo'lmadi" ustuni).
 * Grafik uchun alohida palitra o'ylab topilmaydi.
 *
 * Tashqi bog'liqlik: faqat `react` (+ kit `cx`).
 */

import { forwardRef, useMemo } from 'react'
import { cx } from './primitives.jsx'

/* Siyoh so'nish shkalasi: birinchi bo'lak eng to'q, keyingilari pastga.
   Pastki chegara 34% — undan past bo'lsa bo'lak fondan ajralmay qoladi. */
const INK_TOP = 92
const INK_STEP = 13
const INK_MIN = 34

function inkAt(i) {
  return Math.max(INK_MIN, INK_TOP - i * INK_STEP)
}

/** Bo'lak foni: status ohangi berilgan bo'lsa — status rangi, aks holda siyoh. */
function fillOf(item, i) {
  if (item.tone) return `color-mix(in srgb, var(--ui-st-${item.tone}) 70%, transparent)`
  return `color-mix(in srgb, var(--ui-text-1) ${inkAt(i)}%, transparent)`
}

/** Foizni bir kasr belgisi bilan yozadi: 80.8 → "80,8%", 100 → "100%". */
function pct(v, total) {
  if (!total) return '0%'
  const p = (v / total) * 100
  const s = Number.isInteger(p) ? String(p) : p.toFixed(1)
  return `${s.replace('.', ',')}%`
}

const numFmt = (v) => String(v ?? 0)

/* ─────────────────────────────────────────────────────────────
   DistributionBar
   ───────────────────────────────────────────────────────────── */

/**
 * Taqsimot paneli — ikki ko'rinishda, bitta shartnoma bilan.
 *
 *   variant="rows" — reytingli ro'yxat: yorliq | chiziq | son | ulush.
 *     "43 shablon / 11 turkum", "sabablar bo'yicha belgilangan javoblar",
 *     "shablonlar bo'yicha ishlatilish" — hammasi shu.
 *   variant="bar"  — bitta 100% chiziq: tarkib (narx qaysi turga ketdi).
 *     Bo'laklar ostida afsona turadi, chunki chiziqning o'zida yozuv
 *     sig'maydi va rang yolg'iz qolmasligi kerak.
 *
 * `onSelect` berilsa har bo'lak TUGMA bo'ladi — ya'ni bu ayni paytda
 * filtr boshqaruvi ham. Aynan shu sabab uni SVG bilan chizmaymiz.
 *
 * @param items       [{key, label, value, tone, note}]
 * @param variant     'rows' (standart) | 'bar'
 * @param total       ulush qaysi butundan hisoblanadi (standart — yig'indi)
 * @param max         `rows` da eng uzun chiziq asosi (standart — eng katta qiymat)
 * @param showPct     ulushni ko'rsatish (standart true)
 * @param valueFmt    sonni matnga aylantiruvchi (standart — o'zi)
 * @param onSelect    (key, item) — bosilganda
 * @param selected    tanlangan kalit (chiziq `is-active` bo'ladi)
 * @param emptyLabel  ma'lumot bo'lmaganda ko'rinadigan matn
 * @param label       ro'yxat nomi (ekran o'quvchi uchun)
 */
export const DistributionBar = forwardRef(function DistributionBar(
  {
    items = [],
    variant = 'rows',
    total,
    max,
    showPct = true,
    valueFmt = numFmt,
    onSelect,
    selected,
    emptyLabel = "Ma'lumot yo'q",
    label = 'Taqsimot',
    className,
    ...rest
  },
  ref
) {
  const rows = useMemo(
    () => items.map((it) => ({ ...it, value: Number(it.value) || 0 })),
    [items]
  )
  const sum = useMemo(() => rows.reduce((a, b) => a + b.value, 0), [rows])
  const whole = total != null ? total : sum
  const peak = max != null ? max : rows.reduce((a, b) => Math.max(a, b.value), 0)

  if (!rows.length || sum === 0) {
    return (
      <div ref={ref} className={cx('ui-dist ui-dist--empty', className)} {...rest}>
        {emptyLabel}
      </div>
    )
  }

  /* ── 100% stacked chiziq ── */
  if (variant === 'bar') {
    return (
      <div ref={ref} className={cx('ui-dist ui-dist--bar', className)} {...rest}>
        <div className="ui-dist__track" role="img" aria-label={`${label}: ${rows.map((r) => `${r.label} ${pct(r.value, whole)}`).join(', ')}`}>
          {rows.map((it, i) => {
            const w = whole ? (it.value / whole) * 100 : 0
            if (w <= 0) return null
            const style = { width: `${w}%`, background: fillOf(it, i) }
            const title = `${it.label} — ${valueFmt(it.value)} (${pct(it.value, whole)})`
            return onSelect ? (
              <button
                key={it.key}
                type="button"
                className={cx('ui-dist__seg', selected === it.key && 'is-active')}
                style={style}
                title={title}
                aria-label={title}
                onClick={() => onSelect(it.key, it)}
              />
            ) : (
              <span key={it.key} className="ui-dist__seg" style={style} title={title} />
            )
          })}
        </div>
        <div className="ui-dist__legend">
          {rows.map((it, i) => (
            <span key={it.key} className="ui-dist__leg">
              <span className="ui-dist__swatch" aria-hidden="true" style={{ background: fillOf(it, i) }} />
              <span className="ui-truncate">{it.label}</span>
              <span className="ui-nums ui-dist__leg-val">{valueFmt(it.value)}</span>
            </span>
          ))}
        </div>
      </div>
    )
  }

  /* ── Reytingli ro'yxat ── */
  return (
    <ul ref={ref} className={cx('ui-dist ui-dist--rows', className)} aria-label={label} {...rest}>
      {rows.map((it, i) => {
        const w = peak ? (it.value / peak) * 100 : 0
        const body = (
          <>
            <span className="ui-dist__label ui-truncate">{it.label}</span>
            <span className="ui-dist__track">
              <span className="ui-dist__fill" style={{ width: `${w}%`, background: fillOf(it, i) }} />
            </span>
            <span className="ui-dist__val ui-nums">{valueFmt(it.value)}</span>
            {showPct && <span className="ui-dist__pct ui-nums">{pct(it.value, whole)}</span>}
          </>
        )
        return (
          <li key={it.key} className="ui-dist__row-wrap">
            {onSelect ? (
              <button
                type="button"
                className={cx('ui-dist__row', selected === it.key && 'is-active')}
                onClick={() => onSelect(it.key, it)}
                aria-pressed={selected === it.key}
              >
                {body}
              </button>
            ) : (
              <div className="ui-dist__row">{body}</div>
            )}
            {it.note && <div className="ui-dist__note">{it.note}</div>}
          </li>
        )
      })}
    </ul>
  )
})
DistributionBar.displayName = 'DistributionBar'

/* ─────────────────────────────────────────────────────────────
   Funnel
   ───────────────────────────────────────────────────────────── */

/**
 * Voronka — KETMA-KET qadamlar va ular orasidagi yo'qotish.
 *
 * `DistributionBar` dan farqi ma'noda: u yerda bo'laklar YONMA-YON turadi
 * (butunning qismlari), bu yerda esa har qadam oldingisining ICHIDAN
 * chiqadi. Shuning uchun har qatorda ikkita raqam bor: mutlaq son va
 * oldingi qadamdan qancha qolgani. Yo'qotish alohida ko'rsatiladi
 * (`−14`) — voronkaning butun ma'nosi shu ustunda.
 *
 * @param steps     [{key, label, value, note, tone}] — tartib muhim
 * @param mode      'relative' (standart) — foiz OLDINGI qadamdan
 *                  'first' — foiz birinchi qadamdan
 * @param showLoss  yo'qotish ustuni (standart true)
 * @param valueFmt  sonni matnga aylantiruvchi
 * @param onSelect  (key, step) — bosilganda qator tugma bo'ladi
 * @param note      voronka ostidagi bir qatorlik izoh
 */
export const Funnel = forwardRef(function Funnel(
  {
    steps = [],
    mode = 'relative',
    showLoss = true,
    valueFmt = numFmt,
    onSelect,
    selected,
    note,
    emptyLabel = "Ma'lumot yo'q",
    label = 'Voronka',
    className,
    ...rest
  },
  ref
) {
  const list = useMemo(
    () => steps.map((s) => ({ ...s, value: Number(s.value) || 0 })),
    [steps]
  )
  const base = list.length ? list[0].value : 0

  if (!list.length || base === 0) {
    return (
      <div ref={ref} className={cx('ui-funnel ui-funnel--empty', className)} {...rest}>
        {emptyLabel}
      </div>
    )
  }

  return (
    <div ref={ref} className={cx('ui-funnel', className)} {...rest}>
      <ol className="ui-funnel__list" aria-label={label}>
        {list.map((s, i) => {
          const prev = i > 0 ? list[i - 1].value : null
          const w = base ? (s.value / base) * 100 : 0
          const share = mode === 'first' || i === 0 ? pct(s.value, base) : pct(s.value, prev)
          const loss = prev == null ? null : s.value - prev
          const body = (
            <>
              <span className="ui-funnel__label ui-truncate">{s.label}</span>
              <span className="ui-funnel__track">
                <span
                  className="ui-funnel__fill"
                  style={{ width: `${w}%`, background: fillOf(s, i) }}
                />
              </span>
              <span className="ui-funnel__val ui-nums">{valueFmt(s.value)}</span>
              <span className="ui-funnel__pct ui-nums">{i === 0 ? '' : share}</span>
              {showLoss && (
                <span className={cx('ui-funnel__loss', 'ui-nums', loss < 0 && 'is-loss')}>
                  {loss == null || loss === 0 ? '' : `−${Math.abs(loss)}`}
                </span>
              )}
            </>
          )
          return (
            <li key={s.key} className="ui-funnel__row-wrap">
              {onSelect ? (
                <button
                  type="button"
                  className={cx('ui-funnel__row', selected === s.key && 'is-active')}
                  onClick={() => onSelect(s.key, s)}
                  aria-pressed={selected === s.key}
                >
                  {body}
                </button>
              ) : (
                <div className="ui-funnel__row">{body}</div>
              )}
              {s.note && <div className="ui-funnel__note">{s.note}</div>}
            </li>
          )
        })}
      </ol>
      {note && <div className="ui-funnel__foot">{note}</div>}
    </div>
  )
})
Funnel.displayName = 'Funnel'
